<template>
  <main>
    <section v-if="visible" class="mx-2" style="padding-bottom:10rem;">
      <div v-if="loading" class="text-center">
        <loading />
      </div>
      <div v-if="!loading">
        <b-row>
          <b-col
            v-for="item in forms"
            :key="item.Id"
            cols="12"
            sm="12"
            md="6"
            lg="4"
            xl="4"
            class="mt-3"
          >
            <b-card class="cardStyle mt-4" no-body style="height: 100%">
              <b-card-body class="d-flex flex-column">
                <div class="lineStyle my-2" />
                <div class="formTitle">{{ item['Event_Name__c'] }}</div>
                <form-image :item="item"></form-image>
                <div class="px-0 mt-auto">
                  <p class="my-0">
                    Refer to the Investigational Product (IP) Manual for details on IP storage
                    requirements, shipment receipt and when to report an excursion.
                  </p>
                  <p class="my-0 mt-2">
                    Question about an excursion you already submitted?
                  </p>
                  <p class="my-0">
                    Email
                    <a href="mailto:GCSTEmpExcursionSupport@pfizer.com"
                      >GCSTEmpExcursionSupport@pfizer.com</a
                    >, include your Protocol #, Site # and JTE-XX ticket # (if known).
                  </p>
                </div>
                <b-button
                  class="px-2 buttonText btn-block mt-auto"
                  pill
                  variant="outline-primary"
                  @click="goToFormCreation(item.Id)"
                >
                  {{ creationButton(item['Description__c']) }}
                </b-button>
                <b-button
                  class="pr-5 pl-5 buttonText btn-block"
                  pill
                  variant="outline-secondary"
                  @click="openModal(item)"
                  v-if="user['roles'][0]['name'] === 'Admin'"
                >
                  Add Image
                </b-button>
              </b-card-body>
            </b-card>
          </b-col>
        </b-row>
      </div>

      <b-modal
        id="addImageModal"
        body-class="pt-0"
        centered
        footerClass="p-2 border-top-0"
        headerClass="p-2 mt-2 border-bottom-0"
        hide-footer
        size="lg"
        @ok="$bvModal.hide('addImageModal')"
      >
        <b-row class="justify-content-center ">
          <b-col cols="10" v-if="!modalLoading">
            <b-form-file
              v-model="file"
              :accept="acceptedFileTypes.toString()"
              :state="formStatus"
              class="mb-2 mt-2"
              drop-placeholder="Drop one or more files here..."
              placeholder="Choose one or more files or drop them here..."
            ></b-form-file>
            <div>
              <b-button @click="upload()" :disabled="!file" class="mr-1" size="sm" variant="primary"
                >Upload
              </b-button>
              <b-button @click="file = null" class="mr-2" size="sm" variant="danger"
                >Reset
              </b-button>
            </div>
          </b-col>
          <div v-if="modalLoading">
            <b-spinner
              style="width: 3rem; height: 3rem;"
              label="Large Spinner"
              type="grow"
            ></b-spinner>
          </div>
        </b-row>
        <template #modal-header="{ ok }">
          <p
            class="modal-header-text font-weight-bold pb-0 mb-0 pl-1 mx-auto"
            style="font-size: 18px;"
          >
            Upload Image for {{ selectedEvent.Event_Name__c }}
          </p>
          <b-button class="float-right" variant="link-dark" @click="ok()">
            <b-icon icon="x"></b-icon>
          </b-button>
        </template>
      </b-modal>

      <b-modal
        id="historyModal"
        body-class="pt-0"
        centered
        footerClass="p-2 border-top-0"
        headerClass="p-2 mt-2 border-bottom-0"
        hide-footer
        size="lg"
        @ok="$bvModal.hide('historyModal')"
      >
        <b-row class="justify-content-center ">
          <b-col cols="10">
            <b-table
              id="submitted-form-surveys"
              :busy.sync="busy"
              :current-page="currentPage"
              :fields="fields"
              :items="history"
              :per-page="perPage"
              bordered
              class="rounded-lg text-center mt-3"
              empty-text="There are no history available."
              fixed
              hover
              responsive="sm"
              show-empty
              striped
              table-variant="light"
              ><template #table-busy>
                <div class="text-center text-secondary my-2">
                  <b-spinner class="align-middle"></b-spinner>
                  <strong>Loading...</strong>
                </div>
              </template>
              <template #cell(surveyLastModifiedDate)="data">
                {{ formatDate(data.value) }}
              </template></b-table
            >
            <b-pagination
              v-model="currentPage"
              :per-page="perPage"
              :total-rows="rows"
              align="center"
              aria-controls="submitted-form-surveys"
              pills
              size="sm"
            ></b-pagination>
          </b-col>
        </b-row>
        <template #modal-header="{ ok }">
          <p class="modal-header-text font-weight-bold pb-0 mb-0 pl-1 mx-auto">History</p>
          <b-button class="float-right" variant="link-dark" @click="ok()">
            <b-icon icon="x"></b-icon>
          </b-button>
        </template>
      </b-modal>
    </section>

    <router-view v-else></router-view>
  </main>
</template>

<script>
  import { mapActions } from 'vuex';
  import moment from 'moment';
  import FormImage from './FormImage.vue';

  export default {
    name: 'forms',
    metaInfo() {
      let metaTitle = this.$appName + this.studyId + '-' + this.siteId + ' Forms';
      return {
        title: metaTitle,
      };
    },
    props: {
      user: Object,
    },
    components: {
      FormImage,
    },
    data() {
      return {
        modalLoading: false,
        loading: false,
        forms: [],
        perPage: 5,
        currentPage: 1,
        rows: 0,
        history: [],
        fields: [
          { key: 'surveyCompletedBy', label: 'Email', sortable: true },
          { key: 'surveyLastModifiedDate', label: 'Date', sortable: true },
        ],
        busy: false,
        studyId: '',
        siteId: '',
        file: null,
        formStatus: null,
        acceptedFileTypes: ['image/png'],
        acceptedFileSize: 4e5,
        selectedEvent: null,
      };
    },
    computed: {
      visible() {
        return this.$route.name === 'forms';
      },
    },
    created() {
      if (this.visible) {
        this.getForms();
      }
    },
    watch: {
      $route() {
        if (this.visible) {
          this.getForms();
        }
      },
    },
    methods: {
      ...mapActions(['handleError']),
      creationButton(label) {
        return label ?? 'Report a new form';
      },
      async getForms() {
        this.studyId = this.$route.params['studyId'];
        this.siteId = this.$route.params['siteId'];
        this.loading = true;
        let params = {
          Study: this.studyId,
          Site: this.siteId,
          email: window['Laravel']['user_email'],
        };
        await axios
          .get('/api/get-forms', { params: params })
          .then(r => {
            this.selectedEvent = null;
            this.forms = r.data['records'];
          })
          .catch(e => console.error(e)); // TODO handle catch
        this.loading = false;
      },

      async upload() {
        this.modalLoading = true;
        let data = new FormData();

        data.append('image', this.file);
        data.append('name', this.file.name);
        data.append('eventId', this.selectedEvent.Id);

        await axios
          .post(`/api/upload-form-image`, data)
          .then(response => {
            this.modalLoading = false;
            this.$bvModal.hide('addImageModal');
            this.getForms();
          })
          .catch(error => {
            this.formStatus = false;
            this.modalLoading = false;
            this.handleError(
              error,
              'Something went wrong while uploading, please contact the support team.',
              `Error code: (${error.response.status})`,
            );
            this.loading = false;
          });
      },
      goToFormCreation(id) {
        this.$router.push({
          name: 'form-create',
          params: {
            studyId: this.studyId,
            siteId: this.siteId,
            eventId: id,
          },
        });
      },
      openModal(event) {
        this.selectedEvent = event;
        this.$bvModal.show('addImageModal');
      },
      async getSubmittedSurveys(id) {
        this.$bvModal.show('historyModal');
        this.busy = true;
        try {
          // TODO is this API only returning the logged in users surveys? is that what we want here?
          let response = await axios.get(`/api/get-person-surveys/${id}`);
          this.history = response.data['surveys'];
          this.rows = this.history.length;
        } catch (error) {
          // TODO why is vuex handling the error here?
          await this.$store.dispatch('handleError', {
            vm: this,
            error: error,
            message: "Something went wrong while fetching the person's surveys.",
          });
        }
        this.busy = false;
      },
      formatDate(date) {
        if (date == null || date === '') {
          return '';
        } else {
          return moment(date).format('MMM DD YYYY HH:mm');
        }
      },
    },
  };
</script>

<style scoped>
  .title {
    font-weight: 500;
    font-size: 18px;
    line-height: 28px;
    letter-spacing: 0.25px;
    color: #1c1b1e;
  }

  .subTitle {
    font-weight: 400;
    font-size: 14px;
    line-height: 20px;
    letter-spacing: 0.5px;
    color: #006a68;
  }

  .cardStyle {
    background: #fffbfe;
    box-shadow: 0px 4px 4px -2px rgb(23 38 74 / 8%);
    border-radius: 24px;
    letter-spacing: 0.25px;
  }
  .formTitle {
    font-weight: 400;
    font-size: 20px;
    line-height: 27px;
    color: #01004e;
  }
  .formText {
    font-weight: 400;
    font-size: 13px;
    line-height: 24px;
    color: #6d6d6d;
  }
  .buttonText {
    font-weight: 500;
    font-size: 16px;
    line-height: 24px;
    letter-spacing: 0.5px;
  }
  .lineStyle {
    background: #0060aa;
    border-radius: 2px;
    width: 48px;
    height: 4px;
  }
</style>
